import { TRPCClientError } from '@trpc/client';

/**
 * Reading an error the API sent back — what kind it is, and what may be said about it.
 *
 * ITS OWN MODULE, OUT OF `trpc.ts`, so the query client and the error notices can both depend on
 * it without depending on each other. `trpc.ts` builds the query client, whose global handlers
 * report failures to `errorNotices.ts`; that module needs these readers too, and if they lived in
 * `trpc.ts` the two would import each other. `trpc.ts` re-exports everything here, so no existing
 * import had to change.
 */
// ── Reading errors ───────────────────────────────────────────────────────────

/** The tRPC error code, if this is one of ours. */
export function errorCode(error: unknown): string | undefined {
  if (error instanceof TRPCClientError) {
    const data = error.data as { code?: unknown } | null | undefined;
    return typeof data?.code === 'string' ? data.code : undefined;
  }
  return undefined;
}

/**
 * Which request this was, as the server logged it — the first eight characters of its id.
 *
 * Shown on a notice as its reference, so an operator reporting "it said X" can read out something
 * that finds the exact line in the server log. Eight hex characters, because that is what someone
 * can read over the phone, and within the few minutes around a reported failure it is unique.
 * Null for anything that never reached a procedure.
 */
export function requestReference(error: unknown): string | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data = error.data as { requestId?: unknown } | null | undefined;
  return typeof data?.requestId === 'string' && data.requestId !== ''
    ? data.requestId.slice(0, 8)
    : null;
}

/** No session, or it has expired. The caller's job is to send the user to /login. */
export function isAuthError(error: unknown): boolean {
  return errorCode(error) === 'UNAUTHORIZED';
}

export function isForbidden(error: unknown): boolean {
  return errorCode(error) === 'FORBIDDEN';
}

/**
 * A session that is real but has not satisfied MFA.
 *
 * The server marks this case with a distinct message (see `verifiedProcedure`) so the client
 * can send the user to the code prompt instead of showing them a permission error they cannot
 * do anything about.
 */
export function needsMfa(error: unknown): boolean {
  return isForbidden(error) && error instanceof TRPCClientError && error.message === 'MFA_REQUIRED';
}

/**
 * A session that satisfied MFA once, but not recently enough to move money.
 *
 * Distinct from `needsMfa`: that one means "you have never verified on this session", this one
 * means "you did, this morning". Both send the user to the code prompt, but only this one is
 * something a signed-in, fully-permitted person hits in the middle of their work — so the
 * screen has to say why, or it reads as the application randomly logging them out.
 *
 * See `trustProcedure` and docs/SECURITY.md §2 (fifteen-minute window).
 */
export function needsStepUp(error: unknown): boolean {
  return (
    isForbidden(error) && error instanceof TRPCClientError && error.message === 'STEP_UP_REQUIRED'
  );
}

/**
 * A message safe to put in front of a user.
 *
 * The API's messages are written for people — "This is the last administrator", "That code is
 * not valid" — so they are shown as-is. Anything unrecognised falls back rather than exposing
 * a stack or a driver error, and the fallback is the CALLER's, because "we could not save
 * that" and "we could not load that" are different sentences.
 */
export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof TRPCClientError) {
    // Zod's own message is a JSON blob; the field-level errors are already shown by the form.
    if (errorCode(error) === 'BAD_REQUEST' && error.message.trimStart().startsWith('[')) {
      return fallback;
    }
    if (error.message === 'MFA_REQUIRED') return 'Verify your authenticator code first';
    if (error.message === 'STEP_UP_REQUIRED') {
      return 'Confirm your authenticator code before moving money';
    }
    if (error.message) return error.message;
  }
  return fallback;
}
